<script>
    import { onMount } from "svelte";

    import { user, isMobile } from "../../stores";

    import {
        SearchInput,
        SearchSelect,
        SvelteTable,
        PageLoading,
        Button,
        WorkcoverTrafficModifyModal,
        NotificationModal,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { AustralianStates } from "../../Utils/utilEnums.svelte";

    import {
        replaceURL,
        getText,
        getBooleanIcon,
        isObjectEmpty,
        showError,
        decodeSearchParams,
        encodeSearchParams,
    } from "../../Utils/utilFunctions.svelte";

    import { cacheWorkersCompensation } from "../../Utils/utilCache.svelte";

    import { deleteIcon, editIcon } from "../../Utils/utilConstants.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    import { isEnvApp } from "../../Utils/utilRoute.svelte";

    export let params = {};

    $: isUserEnableModifyWorkcover = $user.role == 100 && isEnvApp();

    let isLoading = false,
        returnDataArr = [],
        filters = { state: "" },
        page = 0,
        pageSize = 25,
        count,
        error;

    let isEditAuthorityModalOpen = false,
        isDeleteAuthorityModalOpen = false,
        isDeletingAuthority = false,
        authorityObj = {
            state: "vic",
            name: "",
            email: "",
            amaRates: Boolean(false),
        };

    $: columns = [
        {
            key: "id",
            title: "ID",
            headerClass:
                "text-left px-3 sortable-header-decoration rounded-tl-xl",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => getText(v.id),
        },
        {
            key: "name",
            title: "Name",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => (v.name ? getText(v.name) : "-"),
        },
        {
            key: "state",
            title: "State",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-default",
            renderValue: (v) =>
                v.state ? getText(v.state.toUpperCase()) : "-",
        },
        {
            key: "email",
            title: "Email",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-default",
            renderValue: (v) => (v.email ? getText(v.email) : "-"),
        },
        {
            key: "amaRates",
            title: "Is AMA Rates",
            headerClass: `text-left px-3 cursor-default ${isUserEnableModifyWorkcover ? "" : "rounded-tr-xl"}`,
            class: "text-left px-3 cursor-default",
            renderValue: (v) => getBooleanIcon(v.amaRates),
            csvContent: (v) => getBooleanIcon(v.amaRates, true),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
            class: "px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
            isHidden: !isUserEnableModifyWorkcover,
            csvExportHidden: true,
        },
    ];

    const sortableHeaderKeys = ["id", "name"];

    const searchFilters = [
        {
            key: "search",
            name: "Search",
            type: "input",
            filterKey: "search",
        },
        {
            key: "state",
            name: "state",
            type: "select",
            filterKey: "filter[state]",
        },
    ];

    onMount(() => {
        if (params.query) updateSearchParamsByQuery(params.query);

        loadData(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1].toString();

                searchFilters.forEach((sf) => {
                    if (arr[i].includes(sf.filterKey))
                        filters[sf.key] = arr[i].split("=")[1];
                });
            }

            updateURL();
        }
    };

    const loadData = async (start, limit, params) => {
        isLoading = true;
        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getWorkCoverAuthorities(start, limit, params)
            .then((res) => _handleData(res))
            .then(() => _updateHeaderSortIcon());

        isLoading = false;
    };

    const deleteAuthority = async () => {
        if (isObjectEmpty(authorityObj) || !authorityObj.id) return;

        isDeletingAuthority = true;

        await apiMethods.deleteMethod
            .deleteWorkCoverAuthority(authorityObj.id)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to delete selected work cover authority, please refresh the page and try again and contact support if the issue persists.",
                    );
                else afterModifyFunc();
            })
            .catch((err) => showError(err));

        isDeletingAuthority = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadData(0, pageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (key == "buttons") {
            if (className.includes("editIcon")) openModifyModal(rowData);
            else if (className.includes("deleteIcon")) openDeleteModal(rowData);
        }
    };

    const handleClickCol = ({ target: { className } }, rowData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;
            updateURL();
            loadData(0, pageSize, _getSearchParams());
        }
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadData(Number(e.detail.firstDataIndex), pageSize, _getSearchParams());
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadData(0, newPageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/WorkCoverAuthorities/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const openModifyModal = (obj) => {
        if (obj && !isObjectEmpty(obj)) authorityObj = { ...obj };

        isEditAuthorityModalOpen = true;
    };

    const openDeleteModal = (obj) => {
        if (obj && !isObjectEmpty(obj)) authorityObj = { ...obj };

        isDeleteAuthorityModalOpen = true;
    };

    const afterModifyFunc = () => {
        closeModifyModal();

        loadData(page * pageSize, pageSize, _getSearchParams());

        cacheWorkersCompensation(true);
    };

    const closeModifyModal = () => {
        authorityObj = {
            state: "vic",
            name: "",
            email: "",
            amaRates: Boolean(false),
        };

        isEditAuthorityModalOpen = false;
        isDeleteAuthorityModalOpen = false;
    };

    // HELPER FUNCTIONS
    const _handleData = async (res) => {
        if (res.error) showError(res.error);
        else {
            returnDataArr = [...res.results];
            count = res.total;
        }
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "name" &&
                            row.childNodes[0].data == "Name") ||
                        (params.sort == "id" && row.childNodes[0].data == "ID")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "nameDesc" &&
                            row.childNodes[0].data == "Name") ||
                        (params.sort == "idDesc" &&
                            row.childNodes[0].data == "ID")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.sort) params.sort = filters.sort;

        searchFilters.forEach((sf) => {
            if (filters[sf.key] || filters[sf.key] === 0)
                params[sf.filterKey] = filters[sf.key];
        });

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;
        }

        return encodeSearchParams(str);
    };

    const _renderActionIcons = () => {
        if (isUserEnableModifyWorkcover)
            return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Workcover Authority">
                        <i class="editIcon ${editIcon}"></i>
                    </div>

                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Workcover Authority">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;
        else return "";
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div class="flex flex-col gap-y-4 flex-0">
        <div class="flex flex-row items-center justify-between flex-wrap">
            <div class="flex gap-4 flex-wrap">
                {#each searchFilters as searchFilter}
                    {#if searchFilter.type == "select"}
                        <SearchSelect
                            bind:value={filters[searchFilter.key]}
                            title={searchFilter.name}
                            onChangeFunc={handleFilterSelect}
                            isLong
                        >
                            <option value="" selected>All States</option>
                            {#each AustralianStates as state}
                                <option value={state.key}>
                                    {state.name}
                                </option>
                            {/each}
                        </SearchSelect>
                    {:else}
                        <SearchInput
                            bind:value={filters[searchFilter.key]}
                            title={searchFilter.name}
                            inputFunc={handleFilterSelect}
                            isLong
                        />
                    {/if}
                {/each}
            </div>

            {#if isUserEnableModifyWorkcover}
                <Button clickFunc={() => openModifyModal()}>
                    Create workcover authority
                </Button>
            {/if}
        </div>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoading}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    columns={columns.filter((col) => !col.isHidden)}
                    rows={returnDataArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnDataArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns: columns.filter((col) => !col.isHidden),
            data: returnDataArr,
            fileName: "WorkCover Authorities",
            allDataApi: async () =>
                await apiMethods.getMethod.getWorkCoverAuthorities(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

<WorkcoverTrafficModifyModal
    type="workcover"
    editObj={authorityObj}
    isModalOpen={isEditAuthorityModalOpen}
    {afterModifyFunc}
    {closeModifyModal}
/>

<NotificationModal
    isModalOpen={isDeleteAuthorityModalOpen}
    title="Warning"
    cancelText="Delete"
    submitText="Cancel"
    confirmButtonClass="btn-accent"
    closeModal={deleteAuthority}
    confirmAction={closeModifyModal}
    isConfirming={isDeletingAuthority}
    isReverseBtns
    closeModalFunction={closeModifyModal}
>
    <div class="flex flex-1 flex-col gap-y-4 overflow-y-scroll">
        <p class="leading-6">
            You are about to delete the selected WorkCover authority:
        </p>

        <div class="flex flex-1 flex-col gap-y-2 p-4">
            <div class="flex flex-1 gap-x-4">
                <p class="w-36">Name:</p>
                <p>{authorityObj.name}</p>
            </div>
            <div class="flex flex-1 gap-x-4">
                <p class="w-36">State:</p>
                <p>{authorityObj.state.toUpperCase()}</p>
            </div>
            <div class="flex flex-1 gap-x-4">
                <p class="w-36">Email:</p>
                <p>{authorityObj.email}</p>
            </div>
            <div class="flex flex-1 gap-x-4">
                <p class="w-36">Is AMA Rates:</p>
                {#if authorityObj.amaRates}
                    <i class="fa fa-check" aria-hidden="true"></i>
                {:else}
                    <i class="fa fa-times" aria-hidden="true"></i>
                {/if}
            </div>
        </div>

        <p class="leading-6">Delete this WorkCover authority?</p>
    </div>
</NotificationModal>
